import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
  Refresh as RefreshIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import {
  fetchPatientReports,
  uploadVcfForPatient,
} from "../../utils/apiPatient";

const MAROON = "#500000";

const extractErrorMessage = (err, statusCode) => {
  const status = statusCode ? ` (HTTP ${statusCode})` : "";

  if (!err) return `An unknown error occurred.${status}`;

  if (
    typeof err.message === "string" &&
    err.message &&
    err.message !== "[object Object]"
  ) {
    return `${err.message}${status}`;
  }

  if (typeof err === "string" && err && err !== "[object Object]") {
    return `${err}${status}`;
  }

  if (statusCode) {
    return `Request failed with HTTP ${statusCode}. Check the browser console for the full response.`;
  }

  return "An unexpected error occurred. Check the browser console for details.";
};

const formatUploadDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return value;
  }
};

const resolveFileName = (report) =>
  report.originalFileName ||
  report.fileName ||
  report.filename ||
  report.name ||
  (() => {
    const url = report.fileUrl || report.url || report.blobUrl || report.storageUrl;
    if (url) {
      try {
        return decodeURIComponent(url).split(/[/?#]/).filter(Boolean).pop() || null;
      } catch { return null; }
    }
    return null;
  })() ||
  "—";

const resolveReportId = (report) =>
  report.reportId ?? report.id ?? report.fileId ?? null;

const GenomicReportsTab = ({ patient }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  const fileInputRef = useRef(null);

  const loadReports = useCallback(async () => {
    if (!patient?.id) return;
    try {
      setLoading(true);
      setFetchError("");
      const data = await fetchPatientReports(patient.id);
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[GenomicReports] fetch error:", err);
      setFetchError(extractErrorMessage(err, err?.response?.status));
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleOpenUploadDialog = () => {
    setSelectedFile(null);
    setUploadError("");
    setUploadSuccess("");
    setUploadProgress(0);
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    if (uploading) return;
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setUploadError("");
    setUploadSuccess("");
    setUploadProgress(0);
  };

  const validateVcfFile = (file) => {
    if (!file) return "No file selected.";
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".vcf") && !name.endsWith(".vcf.gz")) {
      return "Only .vcf or .vcf.gz files are allowed.";
    }
    return null;
  };

  const handleFileChange = (file) => {
    const err = validateVcfFile(file);
    if (err) {
      setUploadError(err);
      setSelectedFile(null);
    } else {
      setUploadError("");
      setSelectedFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileChange(file);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a .vcf or .vcf.gz file before uploading.");
      return;
    }
    const validationErr = validateVcfFile(selectedFile);
    if (validationErr) { setUploadError(validationErr); return; }

    try {
      setUploading(true);
      setUploadError("");
      setUploadSuccess("");
      setUploadProgress(0);

      let uploadResult;
      try {
        uploadResult = await uploadVcfForPatient(patient.id, patient.patientAccountNumber, selectedFile, (pct) => {
          setUploadProgress(pct);
        });
      } catch (uploadErr) {
        console.error("[GenomicReports] upload error:", uploadErr?.message ?? uploadErr);
        throw new Error(uploadErr?.message ?? extractErrorMessage(uploadErr));
      }

      const isDuplicate = uploadResult?.status === "DUPLICATE_FILE";
      setUploadSuccess(isDuplicate
        ? "This file was already uploaded (duplicate detected)."
        : "Report uploaded successfully.");

      await loadReports();

      setTimeout(() => {
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setUploadSuccess("");
        setUploadProgress(0);
      }, 1500);
    } catch (err) {
      console.error("[GenomicReports] upload failed:", err);
      setUploadError(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const mrn = patient?.patientAccountNumber || "—";
  const patientName = patient?.name || "—";

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ color: MAROON, fontWeight: 600 }}>
          Genomic Reports
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={loadReports} disabled={loading} size="small" sx={{ color: MAROON }}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenUploadDialog}
            disabled={!patient?.id}
            sx={{
              backgroundColor: MAROON,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#3a0000" },
            }}
          >
            Upload VCF
          </Button>
        </Box>
      </Box>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFetchError("")}>
          {fetchError}
        </Alert>
      )}

      {/* Reports table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: MAROON }} />
        </Box>
      ) : reports.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 1 }}>
          <FileIcon sx={{ fontSize: 48, color: "#ccc" }} />
          <Typography color="textSecondary">No genomic reports found for this patient.</Typography>
          <Typography variant="body2" color="textSecondary">
            Click <strong>Upload VCF</strong> to add a report.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell sx={{ fontWeight: 700, color: MAROON }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: MAROON }}>Report Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: MAROON }}>Uploaded Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: MAROON }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report, index) => {
                const fileName = resolveFileName(report);
                const reportId = resolveReportId(report);
                return (
                  <TableRow key={reportId ?? index} hover sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FileIcon sx={{ fontSize: 18, color: "#888" }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {fileName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {report.reportType ?? "VCF"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatUploadDate(report.uploadedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.status ?? "ACTIVE"}
                        size="small"
                        sx={{
                          backgroundColor:
                            (report.status ?? "").toUpperCase() === "FAILED" ? "#fdecea" : "#e8f5e9",
                          color:
                            (report.status ?? "").toUpperCase() === "FAILED" ? "#c62828" : "#2e7d32",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: MAROON, fontWeight: 700 }}>Upload Genomic Report</DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Patient
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{patientName}</Typography>
            <Typography variant="body2" color="textSecondary">MRN: {mrn}</Typography>
          </Box>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError("")}>
              {uploadError}
            </Alert>
          )}
          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>{uploadSuccess}</Alert>
          )}

          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            VCF File
          </Typography>

          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragOver ? MAROON : "#ccc"}`,
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: uploading ? "default" : "pointer",
              backgroundColor: dragOver ? "rgba(80,0,0,0.04)" : "#fafafa",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: uploading ? "#ccc" : MAROON,
                backgroundColor: uploading ? "#fafafa" : "rgba(80,0,0,0.04)",
              },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 40, color: "#aaa", mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              Drag &amp; drop a <strong>.vcf</strong> or <strong>.vcf.gz</strong> file here
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ my: 1 }}>or</Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={uploading}
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              sx={{
                textTransform: "none",
                color: MAROON,
                borderColor: MAROON,
                "&:hover": { backgroundColor: MAROON, color: "white" },
              }}
            >
              Choose File
            </Button>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept=".vcf,.vcf.gz"
            hidden
            onChange={handleFileInputChange}
          />

          {selectedFile && (
            <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <FileIcon sx={{ fontSize: 18, color: "#888" }} />
              <Typography variant="body2">
                Selected: <strong>{selectedFile.name}</strong>{" "}
                <Typography component="span" variant="body2" color="textSecondary">
                  ({(selectedFile.size / (1024 * 1024)).toFixed(1)} MB)
                </Typography>
              </Typography>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2" color="textSecondary">Uploading…</Typography>
                <Typography variant="body2" color="textSecondary">{uploadProgress}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  borderRadius: 1,
                  height: 6,
                  backgroundColor: "#f0e0e0",
                  "& .MuiLinearProgress-bar": { backgroundColor: MAROON },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleCloseUploadDialog}
            variant="outlined"
            disabled={uploading}
            sx={{ textTransform: "none", borderColor: MAROON, color: MAROON }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !selectedFile}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              textTransform: "none",
              backgroundColor: MAROON,
              "&:hover": { backgroundColor: "#3a0000" },
            }}
          >
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GenomicReportsTab;
